import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';

import type { MigrationRun, MigrationStatus, RunRequest } from './types';

/** How often the screen asks again while something is going. */
const POLL_MS = 2000;

export const migrationKeys = {
  status: ['admin', 'migration', 'status'] as const,
  runs: ['admin', 'migration', 'runs'] as const,
  run: (id: number) => ['admin', 'migration', 'runs', id] as const,
};

/**
 * The console's own state — docs/05-ADMIN-DASHBOARD.md §5.9.
 *
 * Polled, and only while something is actually running. A console that polled
 * regardless would be asking a Super Admin's browser to count 3,000 media rows
 * every two seconds for as long as the tab is open; one that never polled would
 * need reloading to see a fifty-minute import finish.
 */
export function useMigrationStatus() {
  return useQuery({
    queryKey: migrationKeys.status,
    queryFn: () => apiFetch<{ data: MigrationStatus }>('/admin/migration/status'),
    refetchInterval: (query) => {
      const runs = Object.values(query.state.data?.data.latest ?? {});

      return runs.some((run) => run.status === 'queued' || run.status === 'running')
        ? POLL_MS
        : false;
    },
  });
}

/**
 * One run and its log, polled until it finishes.
 *
 * `enabled` rather than a conditional hook: the log panel is open or it is not,
 * and a hook cannot be called conditionally.
 */
export function useMigrationRun(id: number | null) {
  return useQuery({
    queryKey: migrationKeys.run(id ?? 0),
    queryFn: () => apiFetch<{ data: MigrationRun }>(`/admin/migration/runs/${id}`),
    enabled: id !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.data.status;

      return status === 'queued' || status === 'running' ? POLL_MS : false;
    },
  });
}

export function useMigrationHistory() {
  return useQuery({
    queryKey: migrationKeys.runs,
    queryFn: () => apiFetch<{ data: MigrationRun[] }>('/admin/migration/runs'),
  });
}

/**
 * Starts a command.
 *
 * No optimistic update, unlike every other mutation in the admin (§6.2). An
 * optimistic row would claim a run had started before anything had — and the one
 * thing this screen must never do is say a migration is under way when it is
 * not. It invalidates and waits for the server's answer instead.
 */
export function useRunCommand() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (request: RunRequest) =>
      apiFetch<{ data: MigrationRun }>('/admin/migration/runs', {
        method: 'POST',
        body: request,
      }),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: migrationKeys.status }),
        client.invalidateQueries({ queryKey: migrationKeys.runs }),
      ]);
    },
  });
}
